'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const CABIN_CLASSES = ['Economy', 'Premium Economy', 'Business', 'First'];
const TRIP_TYPES = ['One-way', 'Round-trip', 'Multi-city'];

const POPULAR_ROUTES = [
  { from: 'London (LHR)', to: 'Dubai (DXB)', cabin: 'Business', price: 'From $2,400' },
  { from: 'New York (JFK)', to: 'Paris (CDG)', cabin: 'First', price: 'From $4,800' },
  { from: 'Singapore (SIN)', to: 'Maldives (MLE)', cabin: 'Business', price: 'From $1,200' },
  { from: 'Dubai (DXB)', to: 'Tokyo (NRT)', cabin: 'Business', price: 'From $3,600' },
  { from: 'London (LHR)', to: 'New York (JFK)', cabin: 'First', price: 'From $5,200' },
  { from: 'Sydney (SYD)', to: 'Bali (DPS)', cabin: 'Business', price: 'From $1,800' },
];

const AIRLINES = [
  { name: 'Emirates', logo: '✈', desc: 'World-class Business & First Class suites' },
  { name: 'Singapore Airlines', logo: '✈', desc: 'Award-winning Suites and Business Class' },
  { name: 'Qatar Airways', logo: '✈', desc: 'Qsuite — the world\'s best Business Class' },
  { name: 'Etihad Airways', logo: '✈', desc: 'The Residence and First Apartments' },
  { name: 'Lufthansa', logo: '✈', desc: 'First Class with private suites' },
  { name: 'British Airways', logo: '✈', desc: 'Club Suite and First Class' },
];

export default function FlightsPage() {
  const router = useRouter();
  const [tripType, setTripType] = useState('Round-trip');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [departure, setDeparture] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [cabin, setCabin] = useState('Business');

  const handleSearch = () => {
    const params = new URLSearchParams({
      vertical: 'flight',
      destination: to,
      checkIn: departure,
      checkOut: returnDate,
      travelers: String(passengers),
      budget: cabin,
    });
    router?.push(`/booking?${params?.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/hotels" className="hover:text-[var(--primary)] transition-colors">Hotels</Link>
          <Link href="/activities" className="hover:text-[var(--primary)] transition-colors">Activities</Link>
          <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          <Link href="/private-jets" className="hover:text-[var(--primary)] transition-colors">Private Jets</Link>
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Flights</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            First-Class Itineraries,<br />
            <span className="gold-text-gradient">Quietly Arranged.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-10 max-w-xl">
            One-way, round-trip and multi-city — live airport data with IATA codes. Business and First Class specialists.
          </p>

          {/* Search Widget */}
          <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.15)]">
            {/* Trip type */}
            <div className="flex gap-2 mb-5">
              {TRIP_TYPES?.map((t) => (
                <button
                  key={t}
                  onClick={() => setTripType(t)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${tripType === t ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'}`}>
                  {t}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">From</label>
                <input
                  type="text"
                  placeholder="City or airport (e.g. LHR)"
                  value={from}
                  onChange={(e) => setFrom(e?.target?.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">To</label>
                <input
                  type="text"
                  placeholder="City or airport (e.g. DXB)"
                  value={to}
                  onChange={(e) => setTo(e?.target?.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Cabin Class</label>
                <select
                  value={cabin}
                  onChange={(e) => setCabin(e?.target?.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {CABIN_CLASSES?.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Departure Date</label>
                <input
                  type="date"
                  value={departure}
                  onChange={(e) => setDeparture(e?.target?.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              {tripType !== 'One-way' && (
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Return Date</label>
                  <input
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e?.target?.value)}
                    className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
              )}
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Passengers</label>
                <select
                  value={passengers}
                  onChange={(e) => setPassengers(Number(e?.target?.value))}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5, 6, 8, 10]?.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>)}
                </select>
              </div>
            </div>

            <button onClick={handleSearch} className="btn-primary w-full py-3 rounded-xl text-sm font-semibold">
              Search Flights
            </button>
          </div>
        </div>
      </section>
      {/* Popular Routes */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Popular Routes</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Frequently Booked <span className="gold-text-gradient">Business & First Class</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_ROUTES?.map((r) => (
              <button
                key={`${r?.from}-${r?.to}`}
                onClick={() => { setFrom(r?.from); setTo(r?.to); setCabin(r?.cabin); }}
                className="glass-card rounded-xl p-4 text-left hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-[var(--foreground)]">{r?.from}</span>
                  <span className="text-[var(--primary)]">→</span>
                  <span className="text-sm font-medium text-[var(--foreground)]">{r?.to}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--muted-foreground)]">{r?.cabin}</span>
                  <span className="text-xs font-semibold text-[var(--primary)]">{r?.price}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* Airlines */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Partner Airlines</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>The World&apos;s Finest <span className="gold-text-gradient">Premium Carriers</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {AIRLINES?.map((a) => (
              <div key={a?.name} className="glass-card rounded-xl p-4 flex items-start gap-3">
                <span className="text-2xl">{a?.logo}</span>
                <div>
                  <div className="text-sm font-bold mb-1">{a?.name}</div>
                  <div className="text-xs text-[var(--muted-foreground)]">{a?.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/private-jets" className="hover:text-[var(--primary)] transition-colors">Private Jets</Link>
            <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
